import React, { useEffect, useState } from 'react';
import { CheckCircle, Camera, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Combined photo showing all three poses
const POSES_IMAGE = "https://customer-assets.emergentagent.com/job_fit-life-coach/artifacts/tfekjj6z_IMG_2456.jpeg";

const PhotoGuide = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  const checklist = [
    "Plain background (no clutter)",
    "Be in a well-lit room",
    "Maintain good posture",
    "Take the photo showing the full body at eye level"
  ];

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#1A1A1A]">
        <div className={`container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link to="/" className="inline-flex items-center gap-2 text-[#C9A962] hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Camera className="text-[#C9A962]" size={32} />
              <span className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase">Client Resource</span>
            </div>
            <h1 className="heading-display text-white mb-6">How To Take Before & After Photos</h1>
            <p className="text-lg md:text-xl text-white/90">
              Taking before and after photos is the best way to track your body composition progress. 
              Please try to do this monthly for accurate comparisons.
            </p>
          </div>
        </div>
      </section>

      {/* Photo Poses Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large mb-4 text-center">The Three Essential Angles</h2>
          <p className="body-regular text-[#6B6B6B] text-center mb-12 max-w-2xl mx-auto">
            Take photos from these three angles to capture a complete view of your progress.
          </p>
          
          <div className="max-w-4xl mx-auto">
            <img 
              src={POSES_IMAGE} 
              alt="Front, Side, and Back pose examples"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <h2 className="heading-large mb-4 text-center">Before Taking Photos</h2>
            <p className="body-regular text-[#6B6B6B] text-center mb-12">
              Follow this checklist to ensure consistent, high-quality progress photos.
            </p>
            
            <div className="bg-white p-8 md:p-12">
              <h3 className="heading-small mb-6 flex items-center gap-3">
                <CheckCircle className="text-[#C9A962]" size={24} />
                Photo Checklist
              </h3>
              <ul className="space-y-4">
                {checklist.map((item, index) => (
                  <li key={item} className="flex items-start gap-4 p-4 bg-[#F5F0EB] border-l-4 border-[#C9A962]">
                    <span className="text-2xl font-bold text-[#C9A962]" style={{fontFamily: "'Oswald', sans-serif"}}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="body-regular text-[#1A1A1A] pt-1">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-large mb-8 text-center">Pro Tips for Better Photos</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-3">Timing</h3>
                <p className="text-sm text-[#6B6B6B]">
                  Take photos at the same time of day, ideally in the morning before eating. 
                  This ensures consistency across your progress shots.
                </p>
              </div>
              
              <div className="border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-3">Clothing</h3>
                <p className="text-sm text-[#6B6B6B]">
                  Wear the same fitted clothing in each photo (e.g., shorts and sports bra/fitted top) 
                  to accurately compare changes.
                </p>
              </div>
              
              <div className="border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-3">Lighting</h3>
                <p className="text-sm text-[#6B6B6B]">
                  Natural lighting is best. Avoid harsh overhead lights or shadows that can 
                  distort how your body looks.
                </p>
              </div>
              
              <div className="border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-3">Camera Setup</h3>
                <p className="text-sm text-[#6B6B6B]">
                  Use a timer or ask someone to help. Keep the camera at chest height 
                  and stand the same distance away each time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Reminder */}
      <section className="bg-[#1A1A1A] py-16">
        <div className="container text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="heading-medium text-white mb-4">Remember: Monthly Photos</h2>
            <p className="body-regular text-gray-400 mb-6">
              Taking progress photos monthly gives you the clearest picture of your transformation. 
              Trust the process and stay consistent!
            </p>
            <div className="inline-flex items-center gap-2 bg-[#C9A962] text-[#1A1A1A] px-6 py-3 text-sm font-semibold tracking-[0.1em] uppercase">
              <Camera size={18} />
              Track Your Progress
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="bg-[#F5F0EB] py-8">
        <div className="container text-center">
          <p className="text-xs text-[#6B6B6B]">
            This guide is for Heft Training clients. If you have any questions, reach out to your coach.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PhotoGuide;
